/**
 * Reine Helfer für den Nachrichtenverlauf (Chat-Bubble-Liste).
 *
 * - Datums-Trenner zwischen Tagen ("Heute" / "Gestern" / Datum).
 * - Gruppierung aufeinanderfolgender Nachrichten desselben Absenders innerhalb
 *   eines kurzen Zeitfensters (kein Avatar/Header-Wiederholen).
 *
 * Alle Vergleiche laufen über den lokalen Kalendertag (nicht 24h-Differenz),
 * damit z.B. 23:50 und 00:10 als „verschiedene Tage" gelten.
 */

/** Ob zwei Zeitpunkte auf denselben lokalen Kalendertag fallen. */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Beschriftung für einen Datums-Trenner relativ zu `now`:
 * „Heute" / „Gestern" / sonst lokalisiertes Datum (de-DE, z.B. „14. Juni 2026").
 */
export function dateSeparatorLabel(d: Date, now: Date): string {
  if (isSameDay(d, now)) return 'Heute';

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(d, yesterday)) return 'Gestern';

  return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
}

interface GroupableMessage {
  createdAt: string;
  direction: string;
  senderKind: string;
}

/**
 * Ob `curr` optisch an die vorhergehende Nachricht `prev` angehängt werden soll
 * (selber Absender, kurzes Zeitfenster, selber Tag). Vereinfachte Absender-Identität:
 * gleiche `direction` UND gleicher `senderKind`.
 */
export function shouldGroupWithPrevious(
  prev: GroupableMessage,
  curr: GroupableMessage,
  maxGapMinutes = 5,
): boolean {
  if (prev.direction !== curr.direction) return false;
  if (prev.senderKind !== curr.senderKind) return false;

  const prevAt = new Date(prev.createdAt);
  const currAt = new Date(curr.createdAt);
  if (!isSameDay(prevAt, currAt)) return false;

  const gapMs = Math.abs(currAt.getTime() - prevAt.getTime());
  return gapMs < maxGapMinutes * 60_000;
}
